import React, { useState } from 'react';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import type { Property } from '../../types';
import {
  clearAdvancedMapFilters,
  countAdvancedMapFilters,
  type MapPanelFilters,
} from '../../utils/filterMapProperties';

const TYPE_OPTIONS: { value: Property['type']; label: string }[] = [
  { value: 'appartement', label: 'Appart.' },
  { value: 'maison', label: 'Maison' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'bureau', label: 'Bureau' },
  { value: 'local', label: 'Local' },
];

const STATUS_OPTIONS: { value: Property['status']; label: string }[] = [
  { value: 'disponible', label: 'Disponible' },
  { value: 'bientot', label: 'Bientôt' },
  { value: 'reserve', label: 'Réservé' },
];

const BUDGET_OPTIONS: {
  id: string;
  label: string;
  priceMin?: number;
  priceMax?: number;
}[] = [
  { id: 'all', label: 'Tous budgets' },
  { id: 'under-200', label: '< 200 k€', priceMax: 199_999 },
  { id: '200-400', label: '200 – 400 k€', priceMin: 200_000, priceMax: 400_000 },
  { id: 'over-400', label: '> 400 k€', priceMin: 400_001 },
];

interface MapSidePanelFiltersProps {
  filters: MapPanelFilters;
  onChange: (filters: MapPanelFilters) => void;
  totalPrograms: number;
  filteredPrograms: number;
}

const toggleListValue = <T extends string>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

const getBudgetSelectionId = (filters: MapPanelFilters): string => {
  if (filters.priceMin == null && filters.priceMax == null) return 'all';
  if (filters.priceMax === 199_999) return 'under-200';
  if (filters.priceMin === 200_000 && filters.priceMax === 400_000) return '200-400';
  if (filters.priceMin === 400_001 && filters.priceMax == null) return 'over-400';
  return 'custom';
};

const MapSidePanelFilters: React.FC<MapSidePanelFiltersProps> = ({
  filters,
  onChange,
  totalPrograms,
  filteredPrograms,
}) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const advancedCount = countAdvancedMapFilters(filters);
  const budgetId = getBudgetSelectionId(filters);

  const setBudget = (optionId: string) => {
    const option = BUDGET_OPTIONS.find((item) => item.id === optionId);
    if (!option || optionId === 'all') {
      onChange({ ...filters, priceMin: undefined, priceMax: undefined });
      return;
    }
    onChange({
      ...filters,
      priceMin: option.priceMin,
      priceMax: option.priceMax,
    });
  };

  return (
    <div className="mt-3 space-y-3 border-t border-neutral-100 pt-3">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          type="search"
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
          placeholder="Ville, code postal, nom…"
          className="w-full rounded-lg border border-neutral-200 bg-white py-2 pl-8 pr-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          aria-label="Rechercher un programme"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
          Filtrer
        </p>
        <button
          type="button"
          onClick={() => setShowAdvancedFilters((open) => !open)}
          className={`relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
            showAdvancedFilters
              ? 'border-primary-200 bg-primary-50 text-primary-700'
              : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
          }`}
          aria-expanded={showAdvancedFilters}
          aria-label={
            showAdvancedFilters
              ? 'Masquer les filtres avancés'
              : 'Afficher les filtres avancés'
          }
          title="Filtres type, budget, statut"
        >
          <TuneIcon className="h-[18px] w-[18px]" />
          {advancedCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[9px] font-bold text-white">
              {advancedCount}
            </span>
          )}
        </button>
      </div>

      {showAdvancedFilters && (
        <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
          {advancedCount > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onChange(clearAdvancedMapFilters(filters))}
                className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary-600 hover:text-primary-700"
              >
                <CloseIcon className="h-3.5 w-3.5" />
                Effacer les filtres
              </button>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              Type
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TYPE_OPTIONS.map((option) => {
                const isActive = filters.types.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        types: toggleListValue(filters.types, option.value),
                      })
                    }
                    className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-neutral-50 text-neutral-700 ring-1 ring-neutral-200 hover:bg-neutral-100'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              Budget
            </p>
            <select
              value={budgetId === 'custom' ? 'all' : budgetId}
              onChange={(event) => setBudget(event.target.value)}
              className="w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-2 text-sm text-neutral-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="Filtrer par budget"
            >
              {BUDGET_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              Statut
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((option) => {
                const isActive = filters.status.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        status: toggleListValue(filters.status, option.value),
                      })
                    }
                    className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-neutral-50 text-neutral-700 ring-1 ring-neutral-200 hover:bg-neutral-100'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-neutral-500">
        {filteredPrograms} programme{filteredPrograms > 1 ? 's' : ''} affiché
        {filteredPrograms !== totalPrograms
          ? ` sur ${totalPrograms}`
          : ' sur la carte'}
      </p>
    </div>
  );
};

export default MapSidePanelFilters;
